import { useState, type JSX } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import type {
  DocumentStyle,
  NumberingStyle,
  NumberingStylePosition,
} from '@worldathletics/types';
import { resolveHeadingTitleSeparator } from '@worldathletics/utilities';

import { BlockMenu, BlockReferencedIndicator, EditableText, ExpandToggle } from '@/components/atoms';
import { DocumentAddCtaButton } from '@/components/document/document-add-cta-button';
import { ContainerPrefixInput } from './container-prefix-input';
import { useNodeName } from '@/context/names/names-context';
import type { RootState } from '@/context/store/store';
import {
  moveNode,
  updateNode,
  type DocumentEntity,
} from '@/context/store/slices/documentDataSlice';
import {
  setAddBlockIntent,
  setNewBlockModal,
  setDeleteBlockConfirmationModal,
} from '@/context/store/slices/siteSlice';
import { useSiblingPosition } from '@/hooks/use-sibling-position';
import { useReportActiveBlock } from '@/hooks/use-report-active-block';
import { styleRuleToCssVars, useStyleRule } from '@/lib/style-profile';

import './document-editor-container.scss';

type Props = {
  node: DocumentEntity & { type: 'container' };
  childNodes?: JSX.Element[];
  depth?: number;
  parentStyle?: string | null;
};

// 'book' is intentionally OMITTED from this set: a book container's name is
// always meaningful as just its label (e.g. "Competition Rules") with no
// computed numbering prefix in front. Including it here would render
// "1 — Competition Rules" which adds noise.
const STYLES_WITH_NAME_PREFIX: ReadonlySet<DocumentStyle> =
  new Set<DocumentStyle>([
    'part',
    'appendix',
    'rule',
    'article',
    'section',
    'definition',
  ]);

function DocumentEditorContainer({
  node,
  childNodes,
  depth = 0,
  parentStyle = null,
}: Props) {
  const bookId = useSelector(
    (state: RootState) => state.documentData.sourceNodeKey?.split(':')[0],
  );
  const nodeId = useSelector(
    (state: RootState) => state.documentData.sourceNodeKey?.split(':')[1],
  );
  const dispatch = useDispatch();
  const position = useSiblingPosition(node.id);
  const reportActive = useReportActiveBlock(node);
  const [visibility, setVisibility] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const numberingStyle: NumberingStyle = node.numberingStyle ?? 'free';
  const propagateNumbering = node.propagateNumbering ?? true;
  const continueNumbering = node.continueNumbering ?? false;
  const transparent = node.transparent ?? false;
  const numberingStylePosition: NumberingStylePosition =
    node.numberingStylePosition ?? 'suffix';
  const name = useNodeName(node.id);
  const rule = useStyleRule(node.style, depth);
  const styleVars = styleRuleToCssVars(rule);
  const hasChildren = Boolean(childNodes && childNodes.length > 0);
  const showExpand = hasChildren;
  const showNamePrefix = STYLES_WITH_NAME_PREFIX.has(node.style);
  const placeholder = node.style.charAt(0).toUpperCase() + node.style.slice(1);
  const headingText = node.text ?? '';
  const titleSeparator =
    headingText.trim().length > 0
      ? resolveHeadingTitleSeparator({
          style: node.style,
          parentStyle,
        })
      : '';

  function handleNumberingStyleChange(next: NumberingStyle): void {
    dispatch(updateNode({ id: node.id, changes: { numberingStyle: next } }));
  }

  function handleNumberingStylePositionChange(
    next: NumberingStylePosition,
  ): void {
    dispatch(
      updateNode({ id: node.id, changes: { numberingStylePosition: next } }),
    );
  }

  function handlePropagateNumberingChange(next: boolean): void {
    dispatch(
      updateNode({ id: node.id, changes: { propagateNumbering: next } }),
    );
  }

  function handleContinueNumberingChange(next: boolean): void {
    dispatch(updateNode({ id: node.id, changes: { continueNumbering: next } }));
  }

  function handleTransparentChange(next: boolean): void {
    dispatch(updateNode({ id: node.id, changes: { transparent: next } }));
  }

  function handlePrefixChange(value: string): void {
    dispatch(updateNode({ id: node.id, changes: { prefix: value } }));
  }

  function handleDelete(): void {
    dispatch(
      setDeleteBlockConfirmationModal({
        open: true,
        data: {
          bookId: bookId ?? '',
          nodeId: nodeId ?? '',
          blockId: node.id,
          blockType: node.style,
        },
      }),
    );
  }

  function handleAddChild(): void {
    dispatch(setAddBlockIntent({ mode: 'child', containerId: node.id }));
    dispatch(setNewBlockModal(true));
  }

  function handleAddAbove(): void {
    dispatch(setAddBlockIntent({ mode: 'sibling-before', targetId: node.id }));
    dispatch(setNewBlockModal(true));
  }

  function handleAddBelow(): void {
    dispatch(setAddBlockIntent({ mode: 'sibling-after', targetId: node.id }));
    dispatch(setNewBlockModal(true));
  }

  function handleMoveUp(): void {
    if (!position.canMoveUp) return;
    dispatch(
      moveNode({
        id: node.id,
        newParentId: position.parentId,
        newIndex: position.siblingIndex - 1,
      }),
    );
  }

  function handleMoveDown(): void {
    if (!position.canMoveDown) return;
    dispatch(
      moveNode({
        id: node.id,
        newParentId: position.parentId,
        newIndex: position.siblingIndex + 1,
      }),
    );
  }

  return (
    <div
      data-type="card"
      className={`doc-editor-container is-${node.style} ${isMenuOpen ? 'is-menu-open' : ''}`}
      style={styleVars}
      onFocusCapture={reportActive}
    >
      <button
        type="button"
        className="doc-editor-container__move-btn is-up"
        onClick={handleMoveUp}
        disabled={!position.canMoveUp}
        aria-label="Move up"
        title="Move up"
      >
        <ChevronUpIcon size={16} />
      </button>
      <button
        type="button"
        className="doc-editor-container__move-btn is-down"
        onClick={handleMoveDown}
        disabled={!position.canMoveDown}
        aria-label="Move down"
        title="Move down"
      >
        <ChevronDownIcon size={16} />
      </button>

      <div className="doc-editor-container__header">
        <div className="doc-editor-container__title">
          {showNamePrefix && name && (
            <span className="doc-editor-container__name">
              {numberingStylePosition === 'suffix' ? (
                <>
                  <ContainerPrefixInput
                    className="doc-editor-container__prefix-input"
                    ownerId={node.id}
                    value={node.prefix ?? ''}
                    placeholder={placeholder}
                    onCommit={handlePrefixChange}
                  />{' '}
                  {name}
                </>
              ) : (
                <>
                  {name}{' '}
                  <ContainerPrefixInput
                    className="doc-editor-container__prefix-input"
                    ownerId={node.id}
                    value={node.prefix ?? ''}
                    placeholder={placeholder}
                    onCommit={handlePrefixChange}
                  />
                </>
              )}
              {node.title?.trim() ? ` ${node.title.trim()}` : ''}
              {titleSeparator}
            </span>
          )}
          <EditableText
            nodeId={node.id}
            text={node.text ?? ''}
            className="doc-editor-container__title-text"
          />
        </div>
        <div className="doc-editor-container__toolbar">
          <BlockReferencedIndicator blockId={node.id} />
          {showExpand && (
            <ExpandToggle
              isExpanded={visibility}
              onToggle={() => setVisibility(!visibility)}
              className="doc-editor-container__expand-toggle"
            />
          )}
          <BlockMenu
            variant="container"
            numberingStyle={numberingStyle}
            onNumberingStyleChange={handleNumberingStyleChange}
            numberingStylePosition={numberingStylePosition}
            onNumberingStylePositionChange={handleNumberingStylePositionChange}
            propagateNumbering={propagateNumbering}
            onPropagateNumberingChange={handlePropagateNumberingChange}
            continueNumbering={continueNumbering}
            onContinueNumberingChange={handleContinueNumberingChange}
            transparent={transparent}
            onTransparentChange={handleTransparentChange}
            showTransparentToggle={node.style !== 'subsection'}
            onDelete={handleDelete}
            onAddBlock={handleAddChild}
            onAddAbove={handleAddAbove}
            onAddBelow={handleAddBelow}
            onMoveUp={handleMoveUp}
            onMoveDown={handleMoveDown}
            canMoveUp={position.canMoveUp}
            canMoveDown={position.canMoveDown}
            onOpenChange={setIsMenuOpen}
          />
        </div>
      </div>
      <div
        className="doc-editor-container__content"
        style={visibility ? { display: 'flex' } : { display: 'none' }}
      >
        {childNodes}
        <div className="doc-editor-container__add-block">
          <DocumentAddCtaButton label="Add block" onClick={handleAddChild} />
        </div>
      </div>
    </div>
  );
}

export { DocumentEditorContainer };
